"use client";

import { useState } from "react";
import dayjs from "dayjs";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";
import { useDebouncedCallback } from "use-debounce";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogBody,
  Input,
  EmptyState,
  Spinner,
  Avatar,
  AvatarGroup,
  Tag,
} from "@/ui";
import { useAxios } from "@/app/providers/AxiosProvider";
import useFloats from "@lib/floats/useFloats";
import { FLOAT_STATUS, STATUS_CHIP_DEFAULT } from "@lib/enums";
import isPlaceholderPhoto from "@lib/candidates/isPlaceholderPhoto";
import StatusChip from "@components/Floats/parts/StatusChip";
import styles from "./AddToFloatDialog.module.css";

const buildFloatCandidate = (candidate) => ({
  id: "",
  candidate_id: candidate.id,
  name: candidate.name,
  email: candidate.email,
  status_chip: STATUS_CHIP_DEFAULT,
  position_title: candidate.position_title,
  avatar: candidate.photo_url,
  highlights: candidate.highlights,
  summary: candidate.summary,
  email_summary: candidate.summary,
  tags: candidate.tags,
  useNew: false,
});

export default function AddToFloatDialog({ open, onClose, candidate, onAdded }) {
  const api = useAxios();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [addingUuid, setAddingUuid] = useState(null);

  const { floats, isLoading } = useFloats(
    1,
    { property: "updated_at", direction: "desc" },
    search,
    { status: FLOAT_STATUS.DRAFT }
  );

  const debouncedSetSearch = useDebouncedCallback(setSearch, 300);

  const handleSelectFloat = async (float) => {
    if (addingUuid) return;
    setAddingUuid(float.uuid);
    try {
      const { data: existingFloat } = await api.get(`/floats/${float.uuid}`);

      const alreadyAdded = existingFloat.candidates?.some(
        (c) => c.candidate_id === candidate.id
      );
      if (alreadyAdded) {
        onClose();
        router.push(`/floats/${float.uuid}/edit?step=1`);
        return;
      }

      const updatedFloat = {
        ...existingFloat,
        candidates: [...(existingFloat.candidates ?? []), buildFloatCandidate(candidate)],
      };

      await api.post("/floats", updatedFloat);
      await queryClient.invalidateQueries({ queryKey: ["float", float.uuid] });
      // The draft-float list this dialog renders caches candidate avatars and
      // the "Added" tag, so it goes stale without this.
      await queryClient.invalidateQueries({ queryKey: ["floats"] });

      toast.success(`Added ${candidate.name} to "${existingFloat.name}"`);
      onAdded?.(existingFloat);
      onClose();
      router.push(`/floats/${float.uuid}/edit`);
    } catch (error) {
      console.error("Failed to add candidate to float:", error);
      toast.error("Failed to add candidate to float");
    } finally {
      setAddingUuid(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Add {candidate?.name} to a draft float</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <DialogBody>
            <Input
              placeholder="Search draft floats..."
              fullWidth
              onChange={(e) => debouncedSetSearch(e.target.value)}
              autoFocus
            />
            <div style={{ display: "flex", flexDirection: "column", marginTop: "var(--space-4)" }}>
              {isLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-6) 0" }}>
                  <Spinner />
                </div>
              ) : !floats.length ? (
                <EmptyState
                  icon="🔍"
                  title="No draft floats found"
                  description="Try adjusting your search, or create a new float instead."
                />
              ) : (
                floats.map((float) => {
                  const candidateCount = float.candidates?.length ?? 0;
                  const alreadyAdded = float.candidates?.some(
                    (c) => c.candidate_id === candidate.id
                  );
                  return (
                    <button
                      key={float.uuid}
                      type="button"
                      className={styles.row}
                      disabled={Boolean(addingUuid)}
                      onClick={() => handleSelectFloat(float)}
                      style={{ cursor: addingUuid ? "default" : "pointer" }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", minWidth: 0 }}>
                        <span style={{ font: "var(--text-body2)", fontWeight: 500 }}>{float.name}</span>
                        {float.heading && float.heading !== float.name && (
                          <span
                            style={{
                              font: "var(--text-body3)",
                              color: "var(--text-secondary)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {float.heading}
                          </span>
                        )}
                        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                          {candidateCount > 0 && (
                            <AvatarGroup max={4} size="xs">
                              {float.candidates.map((c) => (
                                <Avatar
                                  key={c.id}
                                  src={isPlaceholderPhoto(c.avatar) ? undefined : c.avatar}
                                  name={c.name}
                                />
                              ))}
                            </AvatarGroup>
                          )}
                          <span style={{ font: "var(--text-body3)", color: "var(--text-secondary)" }}>
                            Created {dayjs(float.created_at).format("D MMM YYYY")}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexShrink: 0 }}>
                        {addingUuid === float.uuid ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            {alreadyAdded && <Tag color="success">Added</Tag>}
                            <StatusChip status={float.status} />
                          </>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </DialogBody>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
